/**
 * Sign-in, following the Receipts split-panel pattern so the two FPS apps
 * feel like one suite.
 *
 * The hierarchy is deliberate and differs from Receipts: there, Google is the
 * only way in. Here the primary audience is CLIENT administrators at IBBA and
 * M&A Source, who sign in by magic link. FPS staff are a handful of people who
 * sign in with Google, so that path is a quiet text link rather than a button
 * competing for attention.
 *
 * The magic link response is identical whether or not the address has an
 * account — the server refuses to say, since that would let anyone enumerate
 * who has access. The confirmation copy therefore says "if that address has
 * access", not "check your inbox", so the UI does not promise something the
 * API deliberately withholds.
 *
 * The Google path is different, and the difference is the point. Sign-up is
 * disabled, so a colleague with a perfectly good Front Porch account who has
 * not been provisioned here signs in at Google, comes back, and lands on the
 * login page as though they had done nothing. Telling THEM why leaks nothing:
 * they have already proven they control the address, and the answer is the
 * same for everyone who is not set up. So that case gets a real explanation
 * and someone to ask, while the magic link path stays deliberately mute.
 */

import { T } from '../lib/copyEdit.tsx';
import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { authClient } from '../lib/auth.ts';
import TurnstileWidget from '../components/TurnstileWidget.tsx';
import Wordmark from '../components/Wordmark.tsx';
import LegalFooter from '../components/LegalFooter.tsx';

type LinkState = { status: 'idle' | 'sending' | 'sent' } | { status: 'error'; message: string };

interface AccessContact {
  name: string | null;
  email: string | null;
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [link, setLink] = useState<LinkState>({ status: 'idle' });
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [contact, setContact] = useState<AccessContact>({ name: null, email: null });
  const [params, setParams] = useSearchParams();

  /* Better Auth appends ?error= when it refuses the callback. The overwhelming
     reason here is that sign-up is disabled and the person has no record, so
     that is what the message says; the code is shown to nobody. */
  const refused = params.get('error');
  /* Null until /api/config says Turnstile is configured, and null again when a
     token expires. The form stays usable either way: the server fails open when
     it cannot reach Cloudflare, so a missing challenge must not be a dead end. */
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileBroken, setTurnstileBroken] = useState(false);
  /* Front Porch's own domain, from /api/config. Addresses on it sign in with
     Google, and the link form is suppressed for them server side. */
  const [staffDomain, setStaffDomain] = useState<string | null>(null);

  useEffect(() => {
    void fetch('/api/config')
      .then((r) => (r.ok ? r.json() : null))
      .then(
        (
          b: {
            accessContact?: AccessContact;
            turnstileSiteKey?: string | null;
            staffEmailDomain?: string;
          } | null,
        ) => {
          if (b?.accessContact) setContact(b.accessContact);
          setTurnstileSiteKey(b?.turnstileSiteKey ?? null);
          setStaffDomain(b?.staffEmailDomain ?? null);
        },
      )
      .catch(() => undefined);
  }, []);

  function dismissRefusal() {
    params.delete('error');
    setParams(params, { replace: true });
  }

  async function handleGoogle() {
    setGoogleError(null);
    dismissRefusal();
    const { error } = await authClient.signIn.social({
      provider: 'google',
      callbackURL: '/',
      /* Without this the refusal lands on Better Auth's own error page, which
         is a dead end with nothing actionable on it. Sending it back here is
         what makes an explanation possible at all. */
      errorCallbackURL: '/login',
    });
    if (error) setGoogleError('Google sign-in is unavailable right now. Try again in a moment.');
  }

  /** True when this address signs in with Google, so the link form would leave
   *  them waiting for something the server deliberately never sends. */
  const isStaffAddress =
    staffDomain !== null &&
    email.trim().toLowerCase().endsWith(`@${staffDomain.toLowerCase()}`);

  async function handleMagicLink(e: FormEvent) {
    e.preventDefault();

    /* Answered here rather than by requesting a link that goes nowhere. The
       server suppresses staff magic links silently and on purpose, so without
       this a colleague waits for an email that will never arrive. Saying so
       reveals nothing: it is true of every address on this domain whether or
       not an account exists. */
    if (isStaffAddress) {
      /* Names the control and where it is. Two earlier versions of this were
         wrong, first pointing at a button that did not exist and then at a
         footer link that has since been replaced by the button below. Copy
         that names a control has to change WITH the control. */
      setLink({
        status: 'error',
        message: `Front Porch staff sign in with Google. Use the "Sign In With Google" button below, with your @${staffDomain} account.`,
      });
      return;
    }

    setLink({ status: 'sending' });

    /* The token travels as a HEADER. The server checks it in front of Better
       Auth's handler, which consumes the raw request stream itself, so reading
       it from the body there would drain the stream and break every auth POST. */
    const { error } = await authClient.signIn.magicLink({
      email: email.trim().toLowerCase(),
      callbackURL: '/',
      fetchOptions: turnstileToken
        ? { headers: { 'x-turnstile-token': turnstileToken } }
        : undefined,
    });

    /* A token is single use. Clearing it means a second attempt asks for a
       fresh challenge rather than replaying one the server will refuse. */
    if (turnstileSiteKey) setTurnstileToken(null);

    // A failure here means the request could not be sent, not that the address
    // was rejected. The server answers identically either way.
    setLink(
      error
        ? { status: 'error', message: 'We could not send that just now. Try again in a moment.' }
        : { status: 'sent' },
    );
  }

  return (
    <div className="login-container">
      <div className="login-brand-panel">
        <div className="login-logo-img">
          <Wordmark />
        </div>
      </div>

      <div className="login-right-panel">
        <div className="login-content">
          <h1 className="login-app-title"><T k="login.title" d="Sponsor Logos" /></h1>

          {/* Someone who authenticated but has no account here. Placed above
              everything so it is not mistaken for a form validation error. */}
          {refused && <AccessNotSetUp contact={contact} />}

          {link.status === 'sent' ? (
            <>
              <p className="login-prompt">
                If <strong>{email.trim().toLowerCase()}</strong> has access, a sign-in link is on
                its way. It works for 15 minutes and can only be used once.
              </p>
              {/* Shown always, never only when the address is unknown, so it
                  stays useful without becoming an answer to "does this account
                  exist?". */}
              <p className="login-hint">
                Nothing arriving? Your account may not be set up yet.{' '}
                <ContactLine contact={contact} />
              </p>
              <hr className="login-divider" />
              <button
                type="button"
                className="login-secondary-btn"
                onClick={() => setLink({ status: 'idle' })}
              >
                Use a Different Address
              </button>
            </>
          ) : (
            <>
              <p className="login-prompt">
                Enter your email and we will send you a sign-in link.
                <br />
                No password to remember.
              </p>

              <hr className="login-divider" />

              {link.status === 'error' && <div className="login-error">{link.message}</div>}

              <form onSubmit={handleMagicLink} className="login-form">
                <label className="visually-hidden" htmlFor="email">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  className="login-input"
                  placeholder="you@yourorganization.org"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <TurnstileWidget
                  siteKey={turnstileSiteKey}
                  onToken={setTurnstileToken}
                  onUnavailable={() => setTurnstileBroken(true)}
                />
                {/* A missing token is a refusal server side, so the button does not
                    offer what would be turned down. Disabled while the challenge is
                    still pending, and while it is broken. */}
                <button
                  type="submit"
                  className="login-primary-btn"
                  disabled={
                    link.status === 'sending' ||
                    (turnstileSiteKey !== null && !turnstileToken)
                  }
                >
                  {link.status === 'sending'
                    ? 'Sending…'
                    : turnstileBroken
                      ? 'Challenge Unavailable'
                      : turnstileSiteKey && !turnstileToken
                        ? 'Complete The Challenge'
                        : 'Email Me a Sign-In Link'}
                </button>
              </form>

              {/*
                Staff sign-in as a real control, not a link in the page footer.
                This is the ONLY route for a super admin, and magic link is
                suppressed for them silently, so a weak affordance here is
                precisely how somebody ends up waiting for an email that will
                never arrive. The Google error was already rendered inside this
                card while its trigger sat in the footer, which was the first
                sign the link was in the wrong place.
              */}
              <hr className="login-divider login-divider--staff" />
              <p className="login-staff-label"><T k="login.staffLabel" d="Front Porch Solutions staff" /></p>
              <button type="button" className="login-secondary-btn" onClick={handleGoogle}>
                Sign In With Google
              </button>

              <p className="login-footer-note"><T k="login.footerNote" d="Access is by invitation. There is no sign-up." /></p>
            </>
          )}

          {googleError && <div className="login-error login-error--staff">{googleError}</div>}
        </div>

        <div className="login-page-footer">
          <LegalFooter variant="page" />
        </div>
      </div>
    </div>
  );
}

/**
 * The explanation a colleague sees after a refused Google sign-in.
 *
 * Says what happened, why, and who to ask — in that order, because the first
 * question is always "did I do something wrong?" and the answer is no.
 */
function AccessNotSetUp({ contact }: { contact: AccessContact }) {
  return (
    <div className="login-notice">
      <p className="login-notice-title"><T k="login.notSetUp" d="Your account is not set up yet" /></p>
      <p>
        You signed in successfully, but you do not have access to Sponsor Logos. Accounts are
        created by Front Porch Solutions, so there is nothing to sign up for.
      </p>
      <p>
        <ContactLine contact={contact} />
      </p>
    </div>
  );
}

/** Who to ask. Falls back to the company when nobody is named in Settings, so
 *  the sentence still reads properly rather than trailing off. */
function ContactLine({ contact }: { contact: AccessContact }) {
  const who = contact.name ?? 'Front Porch Solutions';

  return (
    <>
      Ask {contact.email ? <a href={`mailto:${contact.email}`}>{who}</a> : who} to set you up.
    </>
  );
}
